import { loadStripe } from '@stripe/stripe-js';
import { 
  doc, 
  updateDoc, 
  increment, 
  getDoc,
  collection,
  addDoc
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../firebaseConfig';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const functions = getFunctions();

// Comprehensive Pricing Model
export const FEATURE_PRICING = {
  // Base prices in Euros
  dreamInterpretation: 0.10,
  imageGeneration: 0.10,
  advancedStyleGeneration: 0.20,
  
  // Role-based discounts and limits
  roles: {
    simple: {
      monthlyFreeCredits: 5,
      interpretationDiscount: 0,
      imageGenerationDiscount: 0
    },
    pro: {
      monthlyFreeCredits: 50,
      interpretationDiscount: 0.5,  // 50% off
      imageGenerationDiscount: 0.5  // 50% off
    },
    admin: {
      monthlyFreeCredits: 1000,
      interpretationDiscount: 1,    // Free
      imageGenerationDiscount: 1    // Free
    }
  }
};

export const CREDIT_PACKAGES = [
  { 
    id: 'starter', 
    name: 'Starter Pack', 
    credits: 50, 
    price: 5, 
    bonus: 0,
    description: 'Perfect for beginners' 
  },
  { 
    id: 'pro', 
    name: 'Pro Pack', 
    credits: 120, 
    price: 10, 
    bonus: 10,
    description: '10% bonus credits' 
  },
  { 
    id: 'ultimate', 
    name: 'Ultimate Pack', 
    credits: 270, 
    price: 20, 
    bonus: 30,
    description: '15% bonus credits' 
  }
];

export const calculateFeatureCost = async (feature, userRole = 'simple') => {
  if (!auth.currentUser) {
    throw new Error('User must be logged in');
  }

  const userRef = doc(db, 'profiles', auth.currentUser.uid);
  const userDoc = await getDoc(userRef);
  const userData = userDoc.data();

  // Ensure valid role
  const validRole = userRole in FEATURE_PRICING.roles ? userRole : 'simple';
  
  const basePrice = FEATURE_PRICING[feature] || 0.10; // Default to 0.10 if feature not found
  const roleConfig = FEATURE_PRICING.roles[validRole];
  
  // Check free credits
  if (userData.monthlyFreeCredits > 0) {
    return 0;
  }

  // Safely apply role-based discount
  const discountKey = `${feature}Discount`;
  const discountMultiplier = roleConfig[discountKey] !== undefined 
    ? 1 - roleConfig[discountKey] 
    : 1; // Default to full price if discount not specified

  return basePrice * discountMultiplier;
};

export const initiateStripeCheckout = async (packageIndex) => {
  if (!auth.currentUser) {
    throw new Error('User must be logged in');
  }

  try {
    const selectedPackage = CREDIT_PACKAGES[packageIndex];
    const createCheckoutSession = httpsCallable(functions, 'createStripeCheckoutSession');
    
    const response = await createCheckoutSession({
      packageId: selectedPackage.id,
      userId: auth.currentUser.uid,
      credits: selectedPackage.credits,
      bonus: selectedPackage.bonus,
      price: selectedPackage.price
    });

    // Redirect to Stripe Checkout
    const stripe = await stripePromise;
    const { error } = await stripe.redirectToCheckout({
      sessionId: response.data.sessionId
    });

    if (error) {
      console.error('Stripe Checkout Error:', error);
      throw error;
    }
  } catch (error) {
    console.error('Checkout initiation error:', error);
    throw error;
  }
};

export const handleStripeWebhook = async (event) => {
  try {
    const userRef = doc(db, 'profiles', event.data.object.client_reference_id);
    
    await updateDoc(userRef, {
      credits: increment(event.data.object.metadata.credits),
      monthlyFreeCredits: increment(event.data.object.metadata.bonus)
    });

    // Optional: Log the transaction
    await addDoc(collection(db, 'creditTransactions'), {
      userId: event.data.object.client_reference_id,
      amount: event.data.object.amount_total / 100,
      credits: event.data.object.metadata.credits,
      bonus: event.data.object.metadata.bonus,
      timestamp: new Date()
    });
  } catch (error) {
    console.error('Webhook processing error:', error);
  }
};